import { arrowReaches, isCityCommand, isColumnFull } from "./cityCommand/cityVerbs";
import {
  applySeatCommand,
  type Command,
  type RefusalReason,
  refusalOf,
  type Seat,
} from "./command";
import { markKindAt, marksOf, rushCooldown, taxCooldown } from "./court/general";
import { isFoundingSite } from "./court/king";
import { awaitsGovernor } from "./court/mayors";
import { type AxialCoord, hexEquals } from "./hex";
import { buildPrice, isImprovement } from "./land/build";
import { inCityReach, isCitySite, ownWorkingCity, workingCities } from "./land/cities";
import { findHex, isPlainOwnHex, unitById } from "./run/board";
import { kingOf, supplyOf } from "./run/pools";
import { isHeldUnit } from "./run/seats";
import { sameOwner } from "./run/sides";
import type { BuildKind, FactionId, RunState, UnitKind } from "./types/state";
import { isBroken } from "./units/morale";
import { trainShortfall, unitCount, unitRoom } from "./units/roster";
import { isOrderLocked, routeUnit } from "./units/routes";

/**
 * Why a command would change nothing (CC1): the screen's reader of the door, shown at the pointer
 * before anything is sent. It asks the door (`applyCommand`, `refusalOf`), then each rule's own
 * reasons; no rule reads it. `./command` re-exports it, so the door's importers find it there.
 */

/**
 * The first reason the door would hand the state back for this command (CC1), for the screen to
 * show at the pointer before anything is sent; no rule reads it (the 5.19 precedent). It asks the
 * door itself, so it names a reason exactly when `applySeatCommand` would change nothing: the seat
 * and the court first (`refusalOf`), then the rule's own, else "unchanged".
 */
export function refusalReason(
  state: RunState,
  seat: Seat,
  command: Command,
): RefusalReason | undefined {
  if (applySeatCommand(state, seat, command) !== state) {
    return undefined;
  }
  return refusalOf(state, seat, command) ?? ruleRefusal(state, seat, command) ?? "unchanged";
}

/** The rule's reason for a command the door let through and the rule refused; undefined when it names none. */
function ruleRefusal(state: RunState, seat: Seat, command: Command): RefusalReason | undefined {
  const faction = seat.faction;
  if (state.status !== "running") {
    return "unchanged";
  }
  const cities = isCityCommand(state, faction);
  const ownCity = (coord: AxialCoord): boolean =>
    ownWorkingCity(state, faction, coord) !== undefined;
  switch (command.kind) {
    case "order":
    case "stop":
    case "govern":
    case "aim":
      return unitRefusal(state, seat, command);
    case "promote":
    case "demote":
    case "sell":
    case "enlist":
    case "command":
    case "standDown":
      return unitById(state, command.unitId)?.owner === faction ? undefined : "seat";
    case "train":
      return yardRefusal(state, faction, command.city, command.unit, cities);
    case "build":
      return cities
        ? foundingRefusal(state, faction, command.at)
        : buildRefusal(state, faction, command.at, command.structure);
    case "mark":
      return cities
        ? arrowRefusal(state, faction, command.at, command.from)
        : markRefusal(state, faction, command.at, command.from);
    case "clearMark":
      return cities && !ownCity(command.at) ? "seat" : undefined;
    case "stance":
      return ownCity(command.city) && (cities || findHex(state, command.city)?.mayor)
        ? undefined
        : "seat";
    case "tax":
      return taxCooldown(state, faction) > 0 ? "cooldown" : undefined;
    case "rush":
      if (!ownCity(command.city)) {
        return "seat";
      }
      return rushCooldown(state, faction) > 0 ? "cooldown" : undefined;
    case "moveKing": {
      const king = kingOf(state, faction);
      if (!king) {
        return "seat";
      }
      const there =
        hexEquals(king.coord, command.to) ||
        (king.destination !== undefined && hexEquals(king.destination, command.to));
      return there ? undefined : "noRoad";
    }
    case "stopKing":
      return kingOf(state, faction) ? undefined : "seat";
    default:
      return undefined;
  }
}

/** Why an order, a stop, an errand or an aim of this unit changes nothing. */
function unitRefusal(
  state: RunState,
  seat: Seat,
  command: Extract<Command, { readonly kind: "order" | "stop" | "govern" | "aim" }>,
): RefusalReason | undefined {
  const unit = unitById(state, command.unitId);
  if (
    !unit ||
    !sameOwner(unit.owner, seat.faction) ||
    (isHeldUnit(state, unit.id) && seat.role !== "soldier") ||
    (command.kind === "order" && unit.commander && seat.role === "king") ||
    (command.kind === "aim" && !unit.commander)
  ) {
    return "seat";
  }
  if (command.kind === "aim") {
    const same = unit.target !== undefined && hexEquals(unit.target, command.at);
    return same || routeUnit(state, unit, command.at) ? undefined : "noRoad";
  }
  if (command.kind === "order" && unit.destination && hexEquals(unit.destination, command.to)) {
    return undefined;
  }
  if (isBroken(state, unit) && command.kind !== "govern") {
    return "broken";
  }
  if (isOrderLocked(state, unit)) {
    return "locked";
  }
  if (command.kind === "govern") {
    if (!awaitsGovernor(state, seat.faction, command.city)) {
      return "notSite";
    }
    return routeUnit(state, unit, command.city) ? undefined : "noRoad";
  }
  return command.kind === "order" &&
    !hexEquals(unit.coord, command.to) &&
    !routeUnit(state, unit, command.to)
    ? "noRoad"
    : undefined;
}

/** Why a yard trains nothing: not a working city of the kingdom, busy, no room, the price, no recruit. */
function yardRefusal(
  state: RunState,
  faction: FactionId,
  coord: AxialCoord,
  kind: UnitKind,
  cities: boolean,
): RefusalReason | undefined {
  const city = ownWorkingCity(state, faction, coord);
  if (!city) {
    return "seat";
  }
  if (city.unitBuild !== "none") {
    return "yardBusy";
  }
  if (
    (cities && isColumnFull(state, coord)) ||
    unitCount(state, faction) >= unitRoom(state, faction)
  ) {
    return "room";
  }
  return trainShortfall(state, faction, kind);
}

/** Why a city-command King's founding changes nothing: no site, the price, no road, or the same errand. */
function foundingRefusal(
  state: RunState,
  faction: FactionId,
  at: AxialCoord,
): RefusalReason | undefined {
  const king = kingOf(state, faction);
  if (!king) {
    return "seat";
  }
  if (king.founds && hexEquals(king.founds, at)) {
    return undefined;
  }
  if (!isFoundingSite(state, faction, at)) {
    return "notSite";
  }
  // A new errand gives the old one's price back before it pays.
  const refund = king.founds ? state.rules.cityCost : 0;
  return supplyOf(state, faction) + refund < state.rules.cityCost ? "supply" : "noRoad";
}

/** Why a classic build changes nothing: no place for it, or the price. */
function buildRefusal(
  state: RunState,
  faction: FactionId,
  at: AxialCoord,
  kind: BuildKind,
): RefusalReason | undefined {
  const hex = findHex(state, at);
  if (
    !isPlainOwnHex(hex, faction) ||
    (kind === "city" && !isCitySite(state, faction, at)) ||
    (isImprovement(kind) && (!state.rules.improvements || !inCityReach(state, at)))
  ) {
    return "notSite";
  }
  return supplyOf(state, faction) < buildPrice(state.rules, kind) ? "supply" : undefined;
}

/** Why a city's arrow does not move: not the King's city, a target off the map or burned, or no road to it. */
function arrowRefusal(
  state: RunState,
  faction: FactionId,
  at: AxialCoord,
  from: AxialCoord | undefined,
): RefusalReason | undefined {
  const cities = workingCities(state, faction);
  const city = from && ownWorkingCity(state, faction, from);
  if ((from && !city) || cities.length === 0) {
    return "seat";
  }
  const target = findHex(state, at);
  if (!target || target.razed) {
    return "notSite";
  }
  // Without `from` every city points: none moved means none has a road, or each points there already.
  const pointing = city ? [city] : cities;
  return pointing.some((candidate) => arrowReaches(state, faction, candidate.coord, at))
    ? undefined
    : "noRoad";
}

/** Why a classic banner is not planted: no land for one, a banner already there, a muster point off own land. */
function markRefusal(
  state: RunState,
  faction: FactionId,
  at: AxialCoord,
  from: AxialCoord | undefined,
): RefusalReason | undefined {
  const kind = markKindAt(state, faction, at);
  if (!kind) {
    return "notSite";
  }
  if (marksOf(state, faction).some((mark) => hexEquals(mark.target, at))) {
    return undefined;
  }
  const musterFits =
    from === undefined || (kind === "attack" && markKindAt(state, faction, from) === "defend");
  return musterFits ? undefined : "notSite";
}
